import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import RightModalLayout from '../layouts/Modal/RightModalLayout';
import { getFilteredOptionsByComponent, getFiltersComponentByType } from '../../util/paginationUtils';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';

import { cleanObject } from '../../util/ObjectUtils';

const FiltersSidebar = ({ asideFilters, filterDefaults = {} }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [activeQueryParams, setActiveQueryParams] = React.useState(() => {
    const fromUrl = Object.fromEntries([...searchParams]);
    return { ...filterDefaults, ...fromUrl };
  });
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const getValueFromType = (key, type, value) => {
    switch (type) {
      case 'date-range':
        return {
          [key]: JSON.stringify({
            startDate: value?.value?.startDate?.toISOString(),
            endDate: value?.value?.endDate?.toISOString(),
          })
        };
      case 'amount-comparison':
        return {
          [key]: value?.amount || '',
          [`${key}_comparison`]: value?.comparison || 'greater_than'
        };
      case 'duration-comparison': {
        const result = {};
        const hours = value?.hours;
        const minutes = value?.minutes;
        const seconds = value?.seconds;
        const comparison = value?.comparison || 'greater_than';
        if (hours !== undefined && hours !== '') result[`${key}_hours`] = String(hours);
        if (minutes !== undefined && minutes !== '') result[`${key}_minutes`] = String(minutes);
        if (seconds !== undefined && seconds !== '') result[`${key}_seconds`] = String(seconds);
        result[`${key}_comparison`] = comparison;
        return result;
      }
      default:
        return { [key]: value };
    }
  };

  const onFilterChange = (values) => {
    setActiveQueryParams((prev) => {
      const next = {
        ...prev,
        ...getValueFromType(values.filterKey, values.filterType, values.filterValue)
      };
      (asideFilters || []).forEach((f) => {
        if (f.dependencyKey === values.filterKey) {
          delete next[f.key];
          if (f.type === 'amount-comparison') {
            delete next[`${f.key}_comparison`];
          }
        }
      });
      return next;
    });
  };

  const onApplyFilters = () => {
    const filtersWithoutPage = cleanObject({ ...activeQueryParams });
    delete filtersWithoutPage.page;
    (asideFilters || []).forEach((f) => {
      if (f.type === 'duration-comparison') delete filtersWithoutPage[f.key];
    });
    const queryString = new URLSearchParams(filtersWithoutPage).toString();

    navigate({
      pathname: location.pathname,
      search: `?${queryString}`
    });
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const onRemoveFilters = () => {
    navigate({
      pathname: location.pathname,
      search: ``
    });
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const filtersWithInternalLabel = new Set([
    'select',
    'multi-select',
    'async-paginated-select',
    'async-paginated-multi-select'
  ]);

  // const getFilteredOptions = (filter) => {
  //   if (!filter.dependencyKey) return filter.options;

  //   const selectedDependencyValue = activeQueryParams[filter.dependencyKey];
  //   if (!selectedDependencyValue) return [];

  //   const parentOption = asideFilters
  //     .find((f) => (f.key === filter.dependencyKey))?.options.find((opt) => String(opt.value) === String(selectedDependencyValue));

  //   if (!parentOption) return [];

  //   return parentOption[filter.parentOptionsKey]?.map((option) => ({ ...option, label: option?.name || option?.label || '' })) || [];
  // };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t('filters_sidebar.title')}
        submitLabel={t('filters_sidebar.save_label')}
        cancelLabel={t('filters_sidebar.remove_filters_label')}
        submitAction={onApplyFilters}
        cancelAction={onRemoveFilters}
        footerVariant="VERTICAL"
        className="tw-space-y-4"
      >
        {(asideFilters || []).map((filter, key) => {
          const filteredOptions = getFilteredOptionsByComponent(filter, activeQueryParams, asideFilters);
          const shouldRenderInternalLabel = filtersWithInternalLabel.has(filter.type);
          return (
            <div key={filter.key}>
              {!shouldRenderInternalLabel && filter.label && (
                <div className="tw-text-sm tw-font-semibold tw-mb-2.5">
                  {filter.label}
                </div>
              )}
              {getFiltersComponentByType(
                { ...filter, options: filteredOptions },
                key,
                activeQueryParams,
                onFilterChange,
                searchParams,
                { useFloatingLabel: shouldRenderInternalLabel }
              )}
            </div>
          );
        })}
      </RightModalLayout>

    </div>
  );
};

export default FiltersSidebar;
